import { useEffect, useReducer, useState } from 'react';
import { initContact, initContactValid, toggleAddressData } from 'containers/pages/contacts/data';
import formAddReducer from 'containers/pages/contacts/reducer/formAddReducer';
import formEditReducer from 'containers/pages/contacts/reducer/formEditReducer';
import { createContact, uploadFile, getContactApi, previewFile, archiveContactApi } from 'api';
import { useNotificationProvider } from 'hooks';
import * as TY from 'containers/pages/contacts/constants';
import { handleContact, makeAddressForm, makeAddress, filterContacts, makeNewContactObj, convertContact, updateContact } from 'containers/pages/contacts/libs';
import checkContactValid from 'containers/pages/contacts/libs/checkContactValid';
export default function useContactFormUnified({
  mode = 'create', // 'create' | 'edit'
  id,
  companies,
  // contactTypes,
  languageTypes,
  contacts, // only needed for create (typeahead/established contacts)
  onHandleEstCont = () => {},
  onCloseForm = () => {},
  onFetchContacts = async () => {},
}) {
  const { notifySuccess, notifyError } = useNotificationProvider();

  const reducer = mode === 'edit' ? formEditReducer : formAddReducer;
  const [contact, dispatch] = useReducer(reducer, initContact);

  const [isLoading, setIsLoading] = useState(false);
  const [addressType, setAddressType] = useState(toggleAddressData.buttons[0].value);
  const [valid, setValid] = useState(initContactValid);
  const [canNotSave, setCanNotSave] = useState(true);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [initialContact, setInitialContact] = useState(null); // for edit reset

  const onForm = (e, payload) => {
    handleContact(e, payload, dispatch, addressType, setValid, contact);

    if (mode === 'create' && contacts && onHandleEstCont && e?.target?.name &&
        (e.target.name === TY.FIRST_NAME || e.target.name === TY.LAST_NAME)) {
      const filtered = filterContacts(e.target.value, contacts);
      onHandleEstCont(filtered);
    }
  };

  const onToggleBtn = (e) => setAddressType(e.target.value);

  const onPlace = (placeData) => {
    const madeAddressForm = makeAddressForm(placeData, contact);
    const _madeAddressForm = makeAddress(madeAddressForm, addressType, contact);
    dispatch({ type: addressType, payload: _madeAddressForm });
  };

  const onValid = (key, error, msg) => {
    setValid((prev) => ({ ...prev, [key]: { isErr: error, errMsg: msg, canNotSave: !!error } }));
  };

  const onReset = () => {
    if (mode === 'edit' && initialContact) {
      dispatch({ type: TY.RESET, payload: initialContact });
      checkContactValid(initialContact, setValid);
    } else if (mode === 'create') {
      dispatch({ type: TY.RESET, payload: initContact });
      checkContactValid(initContact, setValid);
    }
  };

  const onSave = async () => {
    if (mode === 'create') {
      setIsLoading(true);
      try {
        let newContact = makeNewContactObj(contact);
        if (contact[TY.IMAGE]?.file) {
          const res = await uploadFile(contact[TY.IMAGE].file);
          if (res?.Success) newContact.VFileId = res.Payload.Id;
        }
        const resCreate = await createContact(newContact);
        if (resCreate?.Success) {
          await onFetchContacts();
          onHandleEstCont?.([]);
          dispatch({ type: TY.RESET, payload: initContact });
          onCloseForm?.();
          notifySuccess('Contact created successfully');
        } else {
          notifyError(resCreate?.Message || 'Failed to create contact');
        }
      } catch (err) {
        notifyError(err?.message || 'Failed to create contact');
      } finally {
        setIsLoading(false);
      }
    } else {
      // edit/update
      await updateContact(setIsLoading, contact, notifySuccess, notifyError, onFetchContacts);
    }
  };

  // Archive (edit only)
  const onArchiveConfirm = () => setShowArchiveConfirm(true);
  const onArchiveCancel = () => setShowArchiveConfirm(false);
  const onArchive = async () => {
    if (mode !== 'edit' || !id) return;
    setIsLoading(true);
    try {
      await archiveContactApi(id);
      await onFetchContacts();
      notifySuccess('Contact archived successfully');
    } catch (error) {
      console.error('Archive error:', error);
      notifyError(`Failed to archive contact: ${error?.message || ''}`);
    } finally {
      setIsLoading(false);
      setShowArchiveConfirm(false);
    }
  };

  // Load existing contact in edit mode
  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      if (mode !== 'edit' || !id) return;
      setIsLoading(true);
      try {
        let _contact = await getContactApi(id);
        if (!cancelled && _contact?.VFileId) {
          try {
            const imgPath = await previewFile(_contact.VFileId);
            _contact.Image = imgPath;
          } catch { /* non-fatal */ }
        }
        if (cancelled) return;
        // const payload = convertContact(_contact, companies, contactTypes, languageTypes);
        const payload = convertContact(_contact, companies, languageTypes);
        checkContactValid(payload, setValid);
        checkContactValid(payload, setValid);
        setInitialContact(payload);
        dispatch({ type: TY.CONTACT_CONVERT, payload });
      } catch (error) {
        if (!cancelled) console.log('Fetching Contact Error :>> ', error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [mode, id, companies, languageTypes]);
  // [mode, id, companies, contactTypes, languageTypes])

  // Compute top-level "canNotSave" when any field reports canNotSave
  useEffect(() => {
    const _canNotSave = Object.values(valid).some(({ canNotSave }) => canNotSave === true);
    setCanNotSave(_canNotSave);
  }, [valid]);

      // ============ ADD THESE LINES BELOW ============
  // Re-validate when companies array changes
  useEffect(() => {
    // Only run when companies array has items
    if (contact[TY.COMPANIES] && contact[TY.COMPANIES].length > 0) {
      // Force validation to re-run
      checkContactValid(contact, setValid);
      
      // Also immediately mark companies field as valid
      setValid((prev) => ({
        ...prev,
        [TY.COMPANIES]: {
          isErr: false,
          errMsg: '',
          canNotSave: false
        }
      }));
    }
  }, [contact[TY.COMPANIES]]); // Only trigger when companies array changes
  // ============ END OF NEW LINES ============

  const state = { contact, valid, isLoading, canNotSave, addressType, showArchiveConfirm };
  const handler = { dispatch, onForm, onToggleBtn, onPlace, onValid, onReset, onSave, onArchiveConfirm, onArchiveCancel, onArchive };

  return { state, handler };
}
